import * as yup from 'yup';
import Encoding from 'encoding-japanese';
import { error } from '../text';

const allowedFileTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
const maxFileSize = 10 * 1024 * 1024; // 10MB
const maxFiles = 30; // デモ・読取パターン用
const maxClassificationFiles = 30; // 自動仕分け用

const maxCsvSize = 20 * 1024 * 1024; // 20MB（辞書機能用）
// マスタCSVは数十万件・数百MB規模になり得るため上限を大きく取る。
// 本体は署名付きURLでGCSへ直接アップロードするため、サーバー側のリクエストサイズ制約は受けない。
const maxMasterCsvSize = 350 * 1024 * 1024; // 350MB（マスタ機能用 / 約30万件を想定）
// エンコーディング判定で全体を読むとメモリ枯渇するため、先頭チャンクのみで判定する。
const encodingCheckChunkSize = 64 * 1024; // 64KB
const allowedCsvTypes = ['text/csv'];

// UTF-8 で先頭チャンクのみを検証する際、末尾でマルチバイト文字が途切れていると
// fatal デコードが誤って失敗する。途中で切れた末尾のバイト列（先頭が 0x80-0xBF の
// 継続バイト、または完結していないリードバイト）を取り除いてから検証するためのヘルパー。
const utf8MaxSequenceLength = 4;
const trimIncompleteUtf8Tail = (bytes: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> => {
  // 末尾から最大4バイトを遡り、マルチバイト列の途中で終わっていれば切り落とす。
  for (let i = bytes.length - 1; i >= 0 && i >= bytes.length - utf8MaxSequenceLength; i--) {
    const byte = bytes[i];
    // 継続バイト（10xxxxxx）はシーケンスの途中なのでスキップして遡る。
    if ((byte & 0b1100_0000) === 0b1000_0000) continue;
    // リードバイトに到達。必要なシーケンス長を求める。
    let expectedLength = 1;
    // 0xxxxxxx（ASCII）
    if ((byte & 0b1000_0000) === 0) expectedLength = 1;
    // 110xxxxx
    else if ((byte & 0b1110_0000) === 0b1100_0000) expectedLength = 2;
    // 1110xxxx
    else if ((byte & 0b1111_0000) === 0b1110_0000) expectedLength = 3;
    // 11110xxx
    else if ((byte & 0b1111_1000) === 0b1111_0000) expectedLength = 4;
    // リードバイト以降に必要な長さ分のバイトが揃っていれば末尾は完結している。
    const available = bytes.length - i;
    return available >= expectedLength ? bytes : bytes.slice(0, i);
  }
  return bytes;
};

const createImageValidation = (
  fileCountLimit: number,
  fileCountErrorMessage: string,
): yup.MixedSchema<File[]> =>
  yup
    .mixed<File[]>()
    .required(error.required)
    .test(
      'notEmpty',
      'ファイルを選択してください',
      files => Array.isArray(files) && files.length > 0,
    )
    .test({
      name: 'fileValidation',
      test: function (files) {
        const errors: string[] = [];

        if (!files.every(file => allowedFileTypes.includes(file.type))) {
          errors.push(error.invalidFileType);
        }
        if (!files.every(file => file.size <= maxFileSize)) {
          errors.push(error.maxSize);
        }
        if (files.length > fileCountLimit) {
          errors.push(fileCountErrorMessage);
        }

        if (errors.length > 0) {
          return this.createError({ message: errors.join('\n') });
        }
        return true;
      },
    });

const imageValidation = createImageValidation(maxFiles, error.maxFiles);

const classificationImageValidation = createImageValidation(
  maxClassificationFiles,
  error.classificationMaxFiles,
);

const csvValidation = yup
  .mixed<File[]>()
  .required(error.required)
  .test('fileType', error.invalidCsvType, files =>
    files.every(file => allowedCsvTypes.includes(file.type)),
  )
  .test('fileSize', error.masterCsvMaxSize, files =>
    files.every(file => file.size <= maxMasterCsvSize),
  )
  .test({
    name: 'fileEncoding',
    message: error.invalidEncoding,
    test: async function (files) {
      // 数百MBのマスタCSVでも安全に判定できるよう、ファイル全体ではなく先頭チャンクのみ検証する。
      const isUtf8 = async (file: File): Promise<boolean> => {
        try {
          const chunk = file.slice(0, encodingCheckChunkSize);
          let bytes = new Uint8Array(await chunk.arrayBuffer());
          // チャンク末尾でマルチバイト文字が途切れると正常な UTF-8 でも誤判定するため、
          // ファイル全体を読み切れていない場合は末尾の不完全なバイト列を切り捨ててから判定する。
          if (file.size > encodingCheckChunkSize) {
            bytes = trimIncompleteUtf8Tail(bytes);
          }
          new TextDecoder('utf-8', { fatal: true }).decode(bytes);
          return true;
        } catch {
          return false;
        }
      };

      const results = await Promise.all(files.map(isUtf8));
      return results.every(Boolean);
    },
  });

/**
 * マスタCSVファイル単体を検証する（ファイル選択時の即時チェック用）。
 * 拡張子/MIME・サイズ・UTF-8 文字コードを検証し、問題があればエラーメッセージを返す。
 * 問題なければ null を返す。新規アップロード・CSV更新の両フローで共通利用する。
 */
export const validateMasterCsvFile = async (file: File): Promise<string | null> => {
  // 拡張子（.csv）と MIME（text/csv）の両方で判定する。
  // file.type は環境により空文字になり得るため、拡張子も併用する。
  // 拡張子は小文字の .csv のみ許可する。大文字（.CSV 等）は後続処理が小文字前提のため弾く（#3050）。
  const hasLowercaseCsvExtension = /\.csv$/.test(file.name);
  const hasUppercaseCsvExtension = !hasLowercaseCsvExtension && /\.csv$/i.test(file.name);
  const hasCsvMime = file.type === '' || allowedCsvTypes.includes(file.type);
  if (hasUppercaseCsvExtension) {
    // 大文字拡張子のみを理由に弾く場合は、原因が分かる専用メッセージを返す。
    return error.uppercaseCsvExtension;
  }
  if (!hasLowercaseCsvExtension || !hasCsvMime) {
    return error.invalidCsvType;
  }

  if (file.size > maxMasterCsvSize) {
    return error.masterCsvMaxSize;
  }

  // UTF-8 判定（先頭チャンクのみ。csvValidation の fileEncoding と同じロジック）。
  try {
    const chunk = file.slice(0, encodingCheckChunkSize);
    let bytes = new Uint8Array(await chunk.arrayBuffer());
    if (file.size > encodingCheckChunkSize) {
      bytes = trimIncompleteUtf8Tail(bytes);
    }
    new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return error.invalidEncoding;
  }

  return null;
};

export const inputUploadSchema = yup.object().shape({
  images: imageValidation,
});

export const classificationUploadSchema = yup.object().shape({
  images: classificationImageValidation,
});

export const inputCsvUploadSchema = yup.object().shape({
  images: csvValidation,
});

// 辞書機能用の CSV バリデーション。
// 辞書はフロントでパースして Firestore に保存するため UTF-8 / Shift-JIS の両方を許可する
// （encoding-japanese で読み込み時に文字コードを自動判定・変換する）。
const dictionaryCsvValidation = yup
  .mixed<File[]>()
  .required(error.required)
  .test('fileType', error.invalidCsvType, files =>
    files.every(file => allowedCsvTypes.includes(file.type)),
  )
  .test('fileSize', error.csvMaxSize, files => files.every(file => file.size <= maxCsvSize))
  .test({
    name: 'fileEncoding',
    message: error.invalidEncoding,
    test: async function (files) {
      // UTF-8 / Shift-JIS（およびその他の日本語エンコーディング）として
      // 判定できれば許可する。判定不能（バイナリ等）のみ弾く。
      const isSupportedEncoding = async (file: File): Promise<boolean> => {
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          const detected = Encoding.detect(bytes);
          return detected === 'UTF8' || detected === 'SJIS' || detected === 'ASCII';
        } catch {
          return false;
        }
      };
      const results = await Promise.all(files.map(isSupportedEncoding));
      return results.every(Boolean);
    },
  });

export const dictionaryCsvUploadSchema = yup.object().shape({
  images: dictionaryCsvValidation,
});
